<template>
  <div v-if="momentDate">
    <b-card
      :key="telescopeModeUser ? 'telescope-mode' : 'regular-mode'"
      :class="telescopeModeUser ? 'telescope-mode' : 'regular-mode'"
      class="tech-availability"
      no-body
    >
      <div class="sub-header text-center">
        <font-awesome-icon :icon="['fal', 'chevron-left']" @click="prevPeriod" />
        <template v-if="containBeforeHours">
          <font-awesome-icon
            v-if="isOffpeak !== 'before'"
            v-tooltip="{
              content: 'Show between 0am to 12pm',
              classes: ['badge-theme'],
              delay: { show: 500, hide: 100 },
            }"
            :icon="['fal', 'arrow-to-left']"
            class="extend-before-hours"
            @click="beforeHours"
          />
          <font-awesome-icon
            v-if="isOffpeak === 'before'"
            :icon="['fal', 'undo']"
            class="extend-before-hours"
            @click="setToPeakHours"
          />
        </template>

        <el-date-picker
          v-model="date"
          :clearable="false"
          :format="datepickerFormat"
          :picker-options="{
            firstDayOfWeek: 1,
          }"
          :type="datepickerType"
          @change="updateDate"
        />

        <div class="date-appendix">
          <span>{{ weekOfYear }}</span>
          <span>{{ dateAppendix }}</span>
        </div>

        <button
          v-if="!isToday || isOffpeak"
          class="btn btn-secondary btn-sm"
          @click.prevent="restoreToday"
        >
          <font-awesome-icon :icon="['fal', 'home']" />
        </button>

        <template v-if="containAfterHours">
          <font-awesome-icon
            v-if="isOffpeak === 'after'"
            :icon="['fal', 'undo']"
            class="extend-after-hours"
            @click="setToPeakHours"
          />
          <font-awesome-icon
            v-if="isOffpeak !== 'after'"
            v-tooltip="{
              content: 'Show between 12pm to 0am',
              classes: ['badge-theme'],
              delay: { show: 500, hide: 100 },
            }"
            :icon="['fal', 'arrow-to-right']"
            class="extend-after-hours"
            @click="afterHours"
          />
        </template>
        <font-awesome-icon :icon="['fal', 'chevron-right']" @click="nextPeriod" />
      </div>

      <div :style="simpleMode ? '' : 'min-height: 816px'" class="card-block">
        <div
          class="header-backdrop"
          :style="$store.state['tech-availability'].lockFilters ? 'background-color: #f08f85' : ''"
        ></div>

        <advanced-filter
          v-if="!simpleMode && !telescopeModeUser"
          :refs="$refs"
          :select-filters="$store.state['tech-availability'].selectFilters"
          @change="filterChanged"
        >
          <div
            v-if="$refs.table && $can('read', 'availabilities', 'scrutiny-lock')"
            class="ml-auto"
            :style="isPaginating ? 'margin-right: 70px' : ''"
          >
            <div class="d-inline-block relative mr-2" style="top: -8px">Scrutiny Lock</div>
            <core-switch
              v-model="$store.state['tech-availability'].lockFilters"
              variant="pill"
              color="danger"
              label
              :default-checked="false"
              :checked="false"
            />
          </div>
        </advanced-filter>

        <div v-if="telescopeModeUser" class="d-flex">
          <div class="ml-auto -mr-3">
            <form-bootstrap-radio
              v-model="telescopeModeType"
              :labelless="true"
              :options="['Week', 'Month']"
              name="telescopeModeType"
            />
          </div>
        </div>

        <div v-if="telescopeModeUser && telescopeModeType === 'Month'">
          <el-calendar v-model="calendarDate">
            <template #dateCell="{ data }">
              <div class="calendar-date">{{ parseInt(data.day.split("-")[2]) }}</div>
              <day-cell-appointment-count :data="data" :month-data="monthData" />
            </template>
          </el-calendar>
        </div>

        <v-server-table
          v-else-if="$store.state['tech-availability'].initialised"
          ref="table"
          :columns="columns"
          :options="options"
          :url="tableUrl"
        />

        <div class="row">
          <div class="col-9">
            <weather-forecast
              v-if="
                $store.state.jobs.currentTab === 1 &&
                $store.state['tech-availability'].shouldFilter &&
                !telescopeModeUser
              "
              :postcode="currentJobPostcode"
              :city="currentJobCity"
              :date="date"
            />
          </div>
          <div class="col-3">
            <div v-if="$refs.table" class="text-right">
              <div>
                <b>{{ currentUsers.length }}</b>
                {{ currentUsers.length > 1 ? "Techs" : "Tech" }}
                <font-awesome-icon
                  v-if="currentUsers.length > 1"
                  :icon="['fal', 'map-marked-alt']"
                  class="cursor text-blue text-20 ml-2"
                  @click="openTechLocator"
                />
              </div>
            </div>
            <div class="text-right">
              <b v-if="telescopeModeUser && telescopeModeType === 'Month'">
                {{ monthlyAppointmentCount }}
              </b>
              <b v-else>{{ tableAppointmentCount }}</b>
              Appointment
            </div>
            <div class="legends">
              <div class="legend legend--unavailable">Unavailable</div>
              <div class="legend legend--public-holiday ml-2">Public Holiday</div>
            </div>
          </div>
        </div>

        <div v-if="highlightUser && highlightUser.id" class="footer">
          <div class="technician-flyout mt-3 mb-5">
            <div class="tech-notes-action">
              <div class="pb-2">
                <span class="pr-2">
                  <user-object
                    :entity-id="highlightUser.id"
                    :name="highlightUser.name"
                    :open-new="true"
                  />
                </span>

                <a
                  v-if="highlightUser.mobile"
                  :href="`tel:${highlightUser.mobile}`"
                  class="cursor mr-1"
                >
                  <font-awesome-icon :icon="['fal', 'mobile']" class="mr-1" />
                  <phone-number :number="highlightUser.mobile" />
                </a>

                <a v-if="highlightUser.email" class="cursor" href="" @click.prevent="chat('mail')">
                  <font-awesome-icon :icon="['fal', 'envelope']" />
                  {{ highlightUser.email }}
                </a>
              </div>
              <button class="btn btn-warning btn-sm" @click.prevent="showUserNoteModal">
                <b>
                  <font-awesome-icon :icon="['fal', 'plus-circle']" />
                  Add a Note
                </b>
              </button>
              <br />
              <div v-if="highlightUser.core_compliance !== 1">
                <div class="pt-1"><b>Core Compliance Violation:</b></div>
                <button
                  v-for="reason in $lodash.map(
                    highlightUser.core_compliance_violations,
                    'violation_reason'
                  )"
                  :key="`reason-${highlightUser.id}-${reason}`"
                  class="btn btn-danger btn-sm mt-1 mr-1"
                  @click.prevent="openComplianceViolationLink(reason)"
                >
                  <font-awesome-icon :icon="['fal', 'exclamation-triangle']" />
                  {{ reason }}
                  <font-awesome-icon
                    v-if="reason === 'Unbanked cash'"
                    :icon="['fal', 'share-square']"
                  />
                </button>
              </div>
              <a class="telescope-switch" href="" @click.prevent="toggleTelescope(highlightUser)">
                <font-awesome-icon v-if="telescopeModeUser" :icon="['fas', 'telescope']" />
                <font-awesome-icon v-else :icon="['fat', 'telescope']" />
              </a>
            </div>

            <div v-if="showDistanceAnalysis" class="distance-analysis mb-5">
              <div class="map-link text-right">
                <a :href="mapUrl" target="_blank">
                  <font-awesome-icon :icon="['fal', 'map-marked']" />
                  Map Overview
                </a>
              </div>

              <div class="pb-3">
                <b>Drive Time Estimates</b>
                <i> for {{ highlightUser.first_name }}'s appointments</i>
              </div>

              <div v-if="driveday" class="pb-3">
                <b>Current Drive Day:</b>
                {{ driveday }} km
              </div>

              <table v-if="currentJobId">
                <tr v-if="$lodash.has(highlightUser, 'addresses[0].city')">
                  <td>
                    [{{ $lodash.get(currentJobAddress, "city") }}] to
                    <b>Home</b>
                    ({{ $lodash.get(highlightUser, "addresses[0].city") }}):
                  </td>
                  <td class="pl-2">
                    <b>
                      <address-distance
                        :key="`${highlightUser.id}-home-${currentJobId}`"
                        :address1="currentJobAddress"
                        :address2="$lodash.get(highlightUser, 'addresses[0].formatted')"
                      />
                    </b>
                  </td>
                </tr>

                <tr
                  v-for="appointment in highlightUser.appointments"
                  :key="`${highlightUser.id}-${appointment.id}`"
                >
                  <td>
                    [{{ $lodash.get(currentJobAddress, "city") }}] to
                    {{ $lodash.get(appointment, "job.job_address.city") }}
                  </td>
                  <td class="pl-2">
                    <b>
                      <address-distance
                        :address1="currentJobAddress"
                        :address2="$lodash.get(appointment, 'job.job_address')"
                      />
                    </b>
                  </td>
                </tr>
              </table>

              <div class="pt-3">
                <b>Service</b><br />
                <i>{{ highlightUser.service_types.map((s) => s.name).join(", ") }}</i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <contact-user ref="contactUser" :invisible="true" :job-id="currentJobId" />
    <b-modal
      id="user-notes"
      ref="userNotesModalRef"
      :hide-footer="true"
      :title="`Add Tech Notes for ${$lodash.get(highlightUser, 'name')}`"
      size="lg"
      @show="userNotesModalShow"
    >
      <b-form
        @keydown="userNotesForm.errors.clear($event.target.name)"
        @submit.prevent="submitUserNotes"
      >
        <textarea
          v-model.trim="userNotesForm.detail"
          class="job-note form-control"
          name="detail"
          placeholder="Type a user note here..."
          rows="3"
        ></textarea>

        <b-card-footer style="border-top: 0">
          <div class="row">
            <div class="col">
              <form-bootstrap-radio
                v-model="userNotesForm.is_related_to_job"
                :options="['Yes', 'No']"
                :single-row="false"
                @input="userNotesForm.errors.clear()"
              >
                Related to a job?
              </form-bootstrap-radio>

              <template v-if="userNotesForm.is_related_to_job === 'Yes'">
                <form-job-select
                  v-model="userNotesForm.related_job"
                  :actual-form="userNotesForm"
                  :filter-by-technician="highlightUser.id"
                  :single-row="false"
                  name="related_job_id"
                  placeholder="Search job..."
                  @change="updateUserNotesRelatedJob"
                >
                  Related Job
                  <template #no-result>
                    No job matching the job number for {{ highlightUser.name }}
                  </template>
                </form-job-select>
              </template>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="d-flex">
                <button :disabled="userNotesFormDisabled" class="btn btn-primary btn-sm mr-3">
                  <font-awesome-icon v-if="userNotesForm.busy" :icon="['fal', 'spinner']" spin />
                  <font-awesome-icon v-else :icon="['fal', 'plus-circle']" />
                  Submit Notes
                </button>
              </div>
            </div>
            <div class="col text-right">
              <div class="btn-group-toggle btn-group btn-group-sm" data-toggle="buttons">
                <label
                  :class="{ active: userNotesForm.action === '' }"
                  class="btn btn-outline-secondary cursor"
                  @click="userNotesForm.action = ''"
                >
                  <input id="action-neutral" v-model="userNotesForm.action" type="radio" value="" />
                  <font-awesome-icon :icon="['fal', 'file-invoice']" />
                </label>
                <label
                  :class="{ active: userNotesForm.action === 'praise' }"
                  class="btn btn-outline-secondary cursor"
                  @click="userNotesForm.action = 'praise'"
                >
                  <input
                    id="action-praise"
                    v-model="userNotesForm.action"
                    type="radio"
                    value="praise"
                  />
                  <font-awesome-icon :icon="['fal', 'thumbs-up']" class="text-primary" />
                </label>
                <label
                  :class="{ active: userNotesForm.action === 'complaint' }"
                  class="btn btn-outline-secondary cursor"
                  @click="userNotesForm.action = 'complaint'"
                >
                  <input
                    id="action-complaint"
                    v-model="userNotesForm.action"
                    type="radio"
                    value="complaint"
                  />
                  <font-awesome-icon :icon="['fal', 'thumbs-down']" class="text-danger" />
                </label>
              </div>
            </div>
          </div>
        </b-card-footer>
      </b-form>
    </b-modal>
  </div>
</template>

<script type="text/babel">
import moment from "moment";
import { Event as TableEvent } from "vue-tables-2";
import EventBus from "../../imports/event-bus";
import externals from "../../constants/externals";
import calendar from "../../mixins/calendar";
import AdvancedFilter from "./AdvancedFilter.vue";
import WeatherForecast from "./WeatherForecast.vue";
import AddressDistance from "./Availability/AddressDistance.vue";
import DayCellAppointmentCount from "./Availability/DayCellAppointmentCount.vue";
import Hour from "./Availability/Hour.vue";
import ContactUser from "./ContactUser.vue";
import distanceService from "@/imports/distance";

const currentHour = (h, heading, hour) => {
  let highlight;
  if (hour === moment().hour()) {
    highlight = <div class="highlight-hour"></div>;
  }

  return (
    <div class="heading-wrapper">
      {highlight}
      {heading}
    </div>
  );
};

export default {
  components: {
    AdvancedFilter,
    AddressDistance,
    ContactUser,
    DayCellAppointmentCount,
    WeatherForecast,
  },
  mixins: [calendar],

  provide() {
    return {
      simpleMode: this.simpleMode,
    };
  },

  props: {
    currentJobId: {
      type: Number,
      required: false,
      default: 0,
    },

    initDate: {
      type: String,
      required: false,
      default: "",
    },

    filterByUserIds: {
      type: Array,
      required: false,
      default: () => [],
    },

    simpleMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      profileDefault:
        "data:image/png;base64,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",

      driveday: null,
      mapUrl: null,
      telescopeModeUser: null,
      telescopeModeType: "Week",
      monthData: null,
      entityName: "user",
      columns: [],
      isOffpeak: false,
      containBeforeHours: false,
      containAfterHours: false,
      date: null,
      calendarDate: null,
      calendarDatePreventWatcherToggle: false,
      today: null,
      startToEnd: {},
      highlightUser: null,
      showDistanceAnalysis: false,
      tableAppointmentCount: null,
      options: {
        filterable: !this.simpleMode,
        advancedFilter: true,
        customFilters: ["hiddenFilters"],
        skin: "table-bordered table-hover",
        sortable: [],
        orderBy: {
          ascending: true,
          column: "first_name",
        },
        perPage: 10,
        perPageValues: [10, 20, 30, 40],
        columnsClasses: {
          name: "name-column",
        },
        headings: {
          "0_am": (h) => currentHour(h, "0 am", 0),
          "1_am": (h) => currentHour(h, "1 am", 1),
          "2_am": (h) => currentHour(h, "2 am", 2),
          "3_am": (h) => currentHour(h, "3 am", 3),
          "4_am": (h) => currentHour(h, "4 am", 4),
          "5_am": (h) => currentHour(h, "5 am", 5),
          "6_am": (h) => currentHour(h, "6 am", 6),
          "7_am": (h) => currentHour(h, "7 am", 7),
          "8_am": (h) => currentHour(h, "8 am", 8),
          "9_am": (h) => currentHour(h, "9 am", 9),
          "10_am": (h) => currentHour(h, "10 am", 10),
          "11_am": (h) => currentHour(h, "11 am", 11),
          "12_pm": (h) => currentHour(h, "12 pm", 12),
          "1_pm": (h) => currentHour(h, "1 pm", 13),
          "2_pm": (h) => currentHour(h, "2 pm", 14),
          "3_pm": (h) => currentHour(h, "3 pm", 15),
          "4_pm": (h) => currentHour(h, "4 pm", 16),
          "5_pm": (h) => currentHour(h, "5 pm", 17),
          "6_pm": (h) => currentHour(h, "6 pm", 18),
          "7_pm": (h) => currentHour(h, "7 pm", 19),
          "8_pm": (h) => currentHour(h, "8 pm", 20),
          "9_pm": (h) => currentHour(h, "9 pm", 21),
          "10_pm": (h) => currentHour(h, "10 pm", 22),
          "11_pm": (h) => currentHour(h, "11 pm", 23),
        },
        templates: {
          name: (h, technician) => {
            const recentAppointmentCount = this.$lodash.get(
              technician,
              "recent_appointments_count"
            );
            const tier = this.$lodash.get(technician, "work_areas[0].tier");
            let nameClass = [
              "name-column__name",
              tier !== undefined ? `tier-${tier}` : null,
              this.highlightUser && technician.id === this.highlightUser.id ? "highlighting" : null,
            ];
            const highlightAUser = () => {
              if (!this.simpleMode) {
                this.highlightAUser(technician);
              }
            };

            let profile;
            if (technician.profile_photo) {
              profile = (
                <img
                  src={technician.profile_photo}
                  alt={technician.name}
                  class="name-column__profile-photo pr-2 rounded-circle"
                />
              );
            } else {
              profile = (
                <img
                  src={this.profileDefault}
                  alt={technician.name}
                  class="name-column__profile-photo pr-2 rounded-circle"
                />
              );
            }

            const skull = h("font-awesome-icon", {
              class: "text-white ml-1",
              props: {
                icon: ["fas", "exclamation-triangle"],
              },
            });

            let wrapperClass =
              technician.core_compliance === 1
                ? "name-column__wrapper"
                : "name-column__wrapper name-column__no-compliance";

            wrapperClass = `${wrapperClass} user_status_${technician.status.toLowerCase()}`;

            if (this.telescopeModeUser) {
              const date = moment(technician.date);
              const week = date.format("ddd");
              const month = date.format("MMM");
              const day = date.format("DD");
              if (date.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD")) {
                nameClass.push("is-today");
              }

              return (
                <div class={wrapperClass}>
                  <div class={nameClass}>
                    <div class="col-6 pl-4 pr-0 date-week">
                      <span>{week}</span>
                    </div>
                    <div class="col-6 pl-1 pr-4 text-center">
                      <b>{day}</b>
                      <br />
                      {month}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div class={wrapperClass}>
                <div class={nameClass} on-click={highlightAUser}>
                  {profile}
                  <div class="w-100 text-center">
                    <b>
                      {technician.first_name}
                      {technician && technician.last_name && technician.last_name.length
                        ? technician.last_name.charAt(0).toUpperCase()
                        : ""}
                    </b>
                    <div>
                      {recentAppointmentCount ? ` ${recentAppointmentCount}` : ""}
                      {technician.core_compliance === 1 ? "" : skull}
                    </div>
                  </div>
                </div>
              </div>
            );
          },

          "0_am": Hour,
          "1_am": Hour,
          "2_am": Hour,
          "3_am": Hour,
          "4_am": Hour,
          "5_am": Hour,
          "6_am": Hour,
          "7_am": Hour,
          "8_am": Hour,
          "9_am": Hour,
          "10_am": Hour,
          "11_am": Hour,
          "12_pm": Hour,
          "1_pm": Hour,
          "2_pm": Hour,
          "3_pm": Hour,
          "4_pm": Hour,
          "5_pm": Hour,
          "6_pm": Hour,
          "7_pm": Hour,
          "8_pm": Hour,
          "9_pm": Hour,
          "10_pm": Hour,
          "11_pm": Hour,
        },
      },

      userNotesForm: new window.AppForm({
        id: "",
        user_id: "",
        action: "",
        detail: "",
        file: "",
        is_related_to_job: "No",
        related_job: "",
        related_job_id: "",
      }),
    };
  },

  computed: {
    isPaginating() {
      if (this.$refs && this.$refs.table) {
        return this.$refs.table.count > 10;
      }

      return false;
    },

    currentUsers() {
      if (this.$lodash.has(this.$refs.table, "data")) {
        return this.$refs.table.data;
      }

      return [];
    },

    currentJobCity() {
      return this.$lodash.get(this.$parent, "$refs.jobEdit.form.job_address.city");
    },

    currentJobPostcode() {
      return this.$lodash.get(this.$parent, "$refs.jobEdit.form.job_address.postcode");
    },

    momentDate() {
      if (!this.date) {
        return null;
      }

      return moment(this.date);
    },

    tableUrl() {
      if (!this.date) {
        return null;
      }

      const momentDate = moment(this.date);

      if (this.telescopeModeUser) {
        const startDate = momentDate.startOf("isoWeek").format("YYYY-MM-DD");
        const endDate = momentDate.endOf("isoWeek").format("YYYY-MM-DD");
        return `/users/technician/availability?mode=telescope&date=${startDate}&end_date=${endDate}&user_ids=${this.telescopeModeUser.id}`;
      }

      return `/users/technician/availability?date=${momentDate.format("YYYY-MM-DD")}&user_ids=${
        this.userIdFilter
      }`;
    },

    monthDataUrl() {
      if (!this.telescopeModeUser || this.telescopeModeType !== "Month") {
        return null;
      }

      const momentDate = moment(this.date);

      const startDate = momentDate.startOf("month").format("YYYY-MM-DD");
      const endDate = momentDate.endOf("month").format("YYYY-MM-DD");
      return `/users/technician/availability?mode=telescope&date=${startDate}&end_date=${endDate}&user_ids=${this.telescopeModeUser.id}`;
    },

    userIdFilter() {
      return this.filterByUserIds.join(",");
    },

    isToday() {
      return this.date === moment().format("YYYY-MM-DD");
    },

    currentJobAddress() {
      return this.$lodash.get(this.$parent, "$refs.jobEdit.form.job_address", {});
    },

    dateAppendix() {
      if (this.telescopeModeUser) {
        return "";
      }

      if (moment().format("YYYY-MM-DD") === this.momentDate.format("YYYY-MM-DD")) {
        return "(Today)";
      }

      if (moment().add(1, "day").format("YYYY-MM-DD") === this.momentDate.format("YYYY-MM-DD")) {
        return "(Tomorrow)";
      }

      if (moment().add(-1, "day").format("YYYY-MM-DD") === this.momentDate.format("YYYY-MM-DD")) {
        return "(Yesterday)";
      }

      return "";
    },

    weekOfYear() {
      if (this.telescopeModeUser) {
        switch (this.telescopeModeType) {
          case "Week": {
            let weekOfYear = this.momentDate.startOf("isoWeek").format("ww");
            const firstDay = this.momentDate.startOf("isoWeek");
            const thisWeek = moment().startOf("isoWeek");

            if (thisWeek.format("YYYY-MM-DD") === firstDay.format("YYYY-MM-DD")) {
              weekOfYear += " - Current Week";
            }

            return `(Week ${weekOfYear})`;
          }

          case "Month":
            return "";
        }
      }

      return "";
    },

    userNotesFormDisabled() {
      if (this.userNotesForm.notReady()) {
        return true;
      }

      if (!this.userNotesForm.detail) {
        return true;
      }

      return (
        this.userNotesForm.is_related_to_job === "Yes" &&
        !Number.isInteger(this.userNotesForm.related_job_id)
      );
    },

    datepickerType() {
      if (!this.telescopeModeUser) {
        return "date";
      }

      return this.telescopeModeType === "Week" ? "week" : "month";
    },

    datepickerFormat() {
      return this.telescopeModeUser ? "MMM yyyy" : "ddd dd MMM yyyy";
    },

    monthlyAppointmentCount() {
      if (!this.telescopeModeUser || this.telescopeModeType !== "Month") {
        return null;
      }

      if (!this.monthData) {
        return null;
      }

      return this.monthData.reduce((total, user) => total + user.appointments.length, 0);
    },
  },

  watch: {
    /* eslint-disable object-shorthand */
    "$route.params.id"() {
      this.resetState();
    },

    initDate() {
      if (this.initDate && this.initDate.length > 0) {
        this.updateDate(moment(this.initDate));
        return;
      }

      this.updateDate(moment());
    },

    calendarDate() {
      if (!this.calendarDate || typeof this.calendarDate.toISOString !== "function") {
        this.date = null;
      }

      this.date = this.calendarDate.toISOString().substring(0, 10);

      if (!this.calendarDatePreventWatcherToggle) {
        this.telescopeModeType = "Week";
      }
    },

    monthDataUrl() {
      this.monthData = null;
      if (!this.monthDataUrl) {
        return;
      }

      this.$http.get(this.monthDataUrl).then((response) => {
        this.monthData = response.body.data;
      });
    },
  },

  mounted() {
    this.reset();

    EventBus.$on("availability-init", () => {
      this.init();
    });
    EventBus.$on("contain-before-hours", () => {
      this.containBeforeHours = true;
    });
    EventBus.$on("contain-after-hours", () => {
      this.containAfterHours = true;
    });
    EventBus.$on("appointment-created", () => {
      this.init();
    });
    EventBus.$on("appointment-updated", () => {
      this.init();
    });
    EventBus.$on("appointment-deleted", () => {
      this.init();
    });
    EventBus.$on("joblist-reset", () => {
      this.highlightUser = null;
      this.driveday = null;
      this.mapUrl = null;
    });

    EventBus.$on("chating-with-user", (payload) => {
      if (this.$refs.contactUser) {
        this.$refs.contactUser.setUser(payload.user);
        this.$refs.contactUser.setChannel(payload.channels);

        this.$refs.contactUser.showModal();
      }
    });

    TableEvent.$on("vue-tables.loading", () => {
      // this.highlightUser = this.telescopeModeUser;

      this.reset();
    });
    TableEvent.$on("vue-tables.loaded", () => {
      this.$store.commit("tech-availability/setTableReference", this.$refs.table);
      this.reset();

      if (this.$lodash.has(this.$refs, "table.query")) {
        this.$watch(
          () => this.$refs.table.query,
          () => {
            this.reset();
          }
        );
      }

      this.updateTableAppointmentCount();
    });

    this.$store.dispatch("tech-availability/initialise");

    this.init().then(() => {
      this.reset();
    });
  },

  methods: {
    init() {
      if (!this.$can("read", "availabilities", "filter-by-brand")) {
        delete this.$store.state["tech-availability"].selectFilters.brand;
      }
      if (!this.$can("read", "availabilities", "filter-by-state")) {
        delete this.$store.state["tech-availability"].selectFilters.state;
      }

      this.reset();

      return new Promise((resolve) => {
        this.reset();

        if (this.date === null) {
          if (this.initDate && this.initDate.length) {
            this.date = this.initDate;
          } else {
            this.date = moment().format("YYYY-MM-DD");
          }
        }
        this.today = moment().format("YYYY-MM-DD");

        this.$store.dispatch("public-holidays/initialise", this.date);

        this.containBeforeHours = false;
        this.containAfterHours = false;

        this.columns = ["name"].concat(this.calendar.dayHours);

        resolve();
      });
    },

    reset() {
      // this.highlightUser = this.telescopeModeUser;

      this.$store.commit("tech-availability/resetAllDrawnIds");
    },

    resetState() {
      this.switchOffTelescope();
      this.highlightUser = null;
      this.telescopeModeUser = null;
      this.driveday = null;
      this.mapUrl = null;
      this.showDistanceAnalysis = false;
      this.userNotesForm.reset();
    },

    refresh() {
      this.$refs.table.refresh();
    },

    prevPeriod() {
      if (!this.telescopeModeUser) {
        return this.updateDate(moment(this.date).add(-1, "day"));
      }

      switch (this.telescopeModeType) {
        case "Week":
          this.updateDate(moment(this.date).add(-7, "day"));
          break;

        case "Month":
          this.updateDate(moment(this.date).add(-1, "month"));
          break;
      }
    },

    nextPeriod() {
      if (!this.telescopeModeUser) {
        return this.updateDate(moment(this.date).add(1, "day"));
      }

      switch (this.telescopeModeType) {
        case "Week":
          this.updateDate(moment(this.date).add(7, "day"));
          break;

        case "Month":
          this.updateDate(moment(this.date).add(1, "month"));
          break;
      }
    },

    restoreToday() {
      this.updateDate();
    },

    toggleFilterLock() {
      this.$store.commit("tech-availability/toggleFilterLock");
    },

    updateDate(date) {
      if (this.isOffpeak) {
        this.setToPeakHours();
      }

      if (date === undefined) {
        this.date = moment().format("YYYY-MM-DD");
      } else {
        this.date = moment(date).format("YYYY-MM-DD");
      }

      this.calendarDatePreventWatcherToggle = true;
      this.calendarDate = new Date(this.date);
      this.$nextTick(() => {
        this.calendarDatePreventWatcherToggle = false;
      });

      this.init().then(() => {
        this.reset();
      });
    },

    beforeHours() {
      this.reset();
      this.columns = ["name"].concat(this.calendar.morningHours);
      this.isOffpeak = "before";
    },

    afterHours() {
      this.reset();
      this.columns = ["name"].concat(this.calendar.afternoonHours);
      this.isOffpeak = "after";
    },

    setToPeakHours() {
      this.reset();
      this.columns = ["name"].concat(this.calendar.dayHours);
      this.isOffpeak = false;
    },

    showUserNoteModal() {
      this.$bvModal.show("user-notes");
    },

    toggleTelescope(user) {
      if (this.telescopeModeUser) {
        return this.switchOffTelescope();
      }

      this.switchOnTelescope(user);
    },

    switchOnTelescope(user) {
      this.telescopeModeUser = user;
      this.options.filterable = false;
      this.$store.commit("tech-availability/resetDrawnUnavailabilityIds");
    },

    switchOffTelescope() {
      this.telescopeModeUser = null;
      this.telescopeModeType = "Week";
      this.options.filterable = !this.simpleMode;
      this.$store.commit("tech-availability/resetDrawnUnavailabilityIds");
    },

    async updateDrivedayAndMapUrl() {
      if (!this.highlightUser.appointments || this.highlightUser.appointments.length === 0) {
        this.driveday = 0;
        return;
      }

      const home = [this.$lodash.get(this.highlightUser, "addresses[0].formatted")];
      const jobLocations = this.highlightUser.appointments.map((appointment) =>
        this.$lodash.get(appointment, "job.job_address.formatted")
      );

      const total = await distanceService(home.concat(jobLocations).concat(home));
      this.driveday = Math.round(total / 100) / 10;

      this.updateMapUrl();
    },

    updateMapUrl() {
      if (!this.highlightUser || !this.highlightUser.appointments) {
        return;
      }

      const mapit = externals.mapit.make("drive-day-overview");
      let name = this.highlightUser.name;
      name = encodeURIComponent(name);
      let brand = this.$lodash.get(this.highlightUser, "brands[0].name");
      brand = encodeURIComponent(brand);
      let home = {
        lat: this.$lodash.get(this.highlightUser, "addresses[0].latitude"),
        lng: this.$lodash.get(this.highlightUser, "addresses[0].longitude"),
        postcode: this.$lodash.get(this.highlightUser, "addresses[0].postcode"),
        address: this.$lodash.get(this.highlightUser, "addresses[0].formatted"),
      };
      home = encodeURIComponent(JSON.stringify(home));
      let newAppointment = {
        lat: this.$lodash.get(this.currentJobAddress, "latitude"),
        lng: this.$lodash.get(this.currentJobAddress, "longitude"),
        postcode: this.$lodash.get(this.currentJobAddress, "postcode"),
        address: this.$lodash.get(this.currentJobAddress, "formatted"),
      };
      newAppointment = encodeURIComponent(JSON.stringify(newAppointment));
      let appointments = this.highlightUser.appointments
        .sort((a, b) =>
          moment(a.time).unix() > moment(b.time).unix()
            ? 1
            : moment(b.time).unix() > moment(a.time).unix()
            ? -1
            : 0
        )
        .map((appointment) => {
          return {
            lat: this.$lodash.get(appointment, "job.job_address.latitude"),
            lng: this.$lodash.get(appointment, "job.job_address.longitude"),
            postcode: this.$lodash.get(appointment, "job.job_address.postcode"),
            address: this.$lodash.get(appointment, "job.job_address.formatted"),
          };
        });
      appointments = encodeURIComponent(JSON.stringify(appointments));
      const distance = this.driveday;

      this.mapUrl = `${mapit}&name=${name}&brand=${brand}&home=${home}&newAppointment=${newAppointment}&appointments=${appointments}&distance=${distance}`;
    },

    highlightAUser(user) {
      this.reset();
      this.userNotesForm.reset();
      this.highlightUser = user;
      this.showDistanceAnalysis = false;

      if (this.$lodash.get(user, "work_areas[0].tier", null) !== null) {
        const uniqueJobAddresses = [];
        const postcodes = [];
        user.appointments.forEach((appointment) => {
          if (!postcodes.includes(appointment.job.job_address.postcode)) {
            postcodes.push(appointment.job.job_address.postcode);

            uniqueJobAddresses.push(appointment.job.job_address);
          }
        });
        this.highlightUser.uniqueJobAddresses = uniqueJobAddresses;
        this.showDistanceAnalysis = true;
      } else {
        this.highlightUser.uniqueJobAddresses = [];
      }

      this.updateDrivedayAndMapUrl();
      this.updateMapUrl();
    },

    openComplianceViolationLink(reason) {
      const dash = externals.dash.domain;
      const userId = this.highlightUser.id;
      const start = moment().subtract(12, "months").startOf("month").format("YYYY-MM-DD");
      const today = moment().format("YYYY-MM-DD");
      const range = `${start},${today}`;
      let mode = "";
      let url = null;

      switch (reason) {
        case "Unbanked cash":
          mode = "unbankedCash";
          url = `${dash}/app/revenue/quotes-invoices?techId=${userId}&dateRange=${range}&mode=${mode}`;
          break;

        case "Documents expired":
          url = `${dash}/app/workflow/documents?bolt_user_id=${userId}`;
          break;

        case "Appointment hygiene":
          // case "Low conversion":
          url = `${dash}/app/workflow/appt-compliance?bolt_user_id=${userId}&dateRange=${range}`;
          break;
      }

      if (url) {
        window.open(url);
      }
    },

    submitUserNotes() {
      if (this.$lodash.has(this.highlightUser, "id") && this.userNotesForm.detail) {
        if (this.userNotesForm.is_related_to_job === "No") {
          this.userNotesForm.related_job = "";
          this.userNotesForm.related_job_id = "";
        }
        this.userNotesForm.user_id = this.highlightUser.id;

        this.userNotesForm.post("/user-notes").then((response) => {
          if (response) {
            this.$bvModal.hide("user-notes");
            window.toastr.success(`Notes added for ${this.highlightUser.name}`, "Success");
            this.userNotesForm.reset();

            EventBus.$emit("user-notes-created");
          }
        });
      }
    },

    updateUserNotesRelatedJob(job) {
      this.userNotesForm.related_job = job;
      this.userNotesForm.related_job_id = job.id;
      this.userNotesForm.errors.clear("related_job_id");
    },

    userNotesModalShow() {
      if (!this.currentJobId) {
        return;
      }

      // To validate if current job has current highlighted tech as assigned tech
      // If yes prepopulate related job field
      const filter = `&technicianId=${this.highlightUser.id}`;

      this.$http
        .get(
          `/jobs?search=${this.currentJobId}&fullSearch=true&category=id&with=brand;customer.primaryContact&limit=50${filter}`
        )
        .then((response) => {
          if (this.$lodash.get(response.body, "data[0].id")) {
            this.updateUserNotesRelatedJob(response.body.data[0]);
          }
        });
    },

    chat(channels) {
      EventBus.$emit("chating-with-user", {
        user: this.highlightUser,
        channels,
      });
    },

    updateTableAppointmentCount() {
      if (this.$lodash.has(this.$refs.table, "data")) {
        this.tableAppointmentCount = this.$refs.table.data.reduce(
          (total, user) => total + user.appointments.length,
          0
        );

        return;
      }

      this.tableAppointmentCount = null;
    },

    openTechLocator() {
      const mapit = externals.mapit.make("pin-users");
      const users = this.currentUsers
        .filter((user) => this.$lodash.has(user, "primary_work_areas[0].postcode"))
        .map((user) => ({
          name: user.name,
          brand: this.$lodash.get(user, "brands[0].name", ""),
          tier_0: this.$lodash.get(user, "primary_work_areas[0].postcode", ""),
        }));
      const userString = encodeURIComponent(JSON.stringify(users));

      if (users.length < this.currentUsers.length) {
        window.toastr.warning("Some users are skipped due to missing Tier 0 postcode", "Warning");
      }

      window.open(`${mapit}&users=${userString}`);
    },

    filterChanged(change) {
      this.$store.commit("tech-availability/selectFiltersChanged", change);
    },
  },
};
</script>
